import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AtSign,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Clock3,
  Cloud,
  Copy,
  ExternalLink,
  Globe2,
  Inbox,
  MailPlus,
  PauseCircle,
  PlayCircle,
  RefreshCw,
  ServerCog,
  ShieldCheck,
  Trash2,
  XCircle,
} from 'lucide-react';
import { api } from '../api';
import { formatTime, Modal, PageHeader } from '../components/Common';
import { writeClipboardText } from '../utils/clipboard';
import type { DomainMailbox, DomainMailDomain, Message, Pagination } from '../types';

type Notify = (message: string, type?: 'success' | 'error') => void;

const MAILBOX_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const DEFAULT_MAILBOX_PAGE_SIZE = 20;

const statusText: Record<string, string> = {
  active: '已接通',
  pending: '待配置',
  error: '异常',
  disabled: '已停用',
};

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function domainStatusClass(status: string): string {
  if (status === 'active') return 'domain-status-active';
  if (status === 'error') return 'domain-status-error';
  return 'domain-status-pending';
}

function domainTypeText(domain: DomainMailDomain): string {
  if (!domain.cloudflareZoneName) return '等待发现父 Zone';
  if (!domain.isSubdomain) return '根域名';
  if (domain.domainLevel === 1) return '二级域名';
  if (domain.domainLevel === 2) return '三级域名';
  return `${domain.domainLevel + 1} 级子域名`;
}

function expiresText(mailbox: DomainMailbox): string {
  if (mailbox.lifecycle === 'permanent') return '永久有效';
  if (!mailbox.expiresAt) return '未设置';
  const remaining = new Date(mailbox.expiresAt).getTime() - Date.now();
  if (remaining <= 0) return '已过期';
  const minutes = Math.ceil(remaining / 60_000);
  if (minutes < 60) return `${minutes} 分钟后`;
  const hours = Math.ceil(minutes / 60);
  if (hours < 48) return `${hours} 小时后`;
  return `${Math.ceil(hours / 24)} 天后`;
}

/** 功能：管理自有域名、Cloudflare 收件链路和随机域名邮箱。 */
export function DomainMailScreen({ notify }: { notify: Notify }) {
  const [domains, setDomains] = useState<DomainMailDomain[]>([]);
  const [mailboxes, setMailboxes] = useState<DomainMailbox[]>([]);
  const [selectedDomainId, setSelectedDomainId] = useState(0);
  const [mailboxPage, setMailboxPage] = useState(1);
  const [mailboxPageSize, setMailboxPageSize] = useState(DEFAULT_MAILBOX_PAGE_SIZE);
  const [mailboxPagination, setMailboxPagination] = useState<Pagination>({ page: 1, pageSize: DEFAULT_MAILBOX_PAGE_SIZE, total: 0, totalPages: 1 });
  const mailboxRequestId = useRef(0);
  const firstMailboxLoad = useRef(true);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState('');
  const [domainOpen, setDomainOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [activeMailbox, setActiveMailbox] = useState<DomainMailbox | null>(null);
  const [domainForm, setDomainForm] = useState({ domain: '', cloudflareToken: '', webhookBaseUrl: window.location.origin });
  const [generateForm, setGenerateForm] = useState({ domainId: '', count: '10', lifecycle: 'temporary', ttlMinutes: '60', localLength: '12', category: 'safe' });

  const loadDomains = useCallback(async (): Promise<DomainMailDomain[]> => {
    const data = await api<DomainMailDomain[]>('/api/domain-mail/domains');
    setDomains(data);
    return data;
  }, []);

  const loadMailboxes = useCallback(async (domainId: number, page: number, pageSize: number): Promise<void> => {
    const requestId = ++mailboxRequestId.current;
    const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
    if (domainId > 0) params.set('domainId', String(domainId));
    const data = await api<{
      items: DomainMailbox[];
      total: number;
      page?: number;
      pageSize?: number;
      totalPages?: number;
    }>('/api/domain-mail/mailboxes?' + params.toString());
    if (requestId !== mailboxRequestId.current) return;
    const resolvedPageSize = MAILBOX_PAGE_SIZE_OPTIONS.includes(Number(data.pageSize) as (typeof MAILBOX_PAGE_SIZE_OPTIONS)[number])
      ? Number(data.pageSize)
      : pageSize;
    const total = Math.max(0, Number(data.total || 0));
    const totalPages = Math.max(1, Number(data.totalPages || Math.ceil(total / resolvedPageSize)));
    const resolvedPage = Math.min(Math.max(1, Number(data.page || page)), totalPages);
    setMailboxes(data.items);
    setMailboxPage(resolvedPage);
    setMailboxPageSize(resolvedPageSize);
    setMailboxPagination({ page: resolvedPage, pageSize: resolvedPageSize, total, totalPages });
  }, []);

  const refreshAll = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      await Promise.all([loadDomains(), loadMailboxes(selectedDomainId, mailboxPage, mailboxPageSize)]);
    } catch (error) {
      notify(errorMessage(error, '域名邮箱数据加载失败'), 'error');
    } finally {
      setLoading(false);
    }
  }, [loadDomains, loadMailboxes, mailboxPage, mailboxPageSize, notify, selectedDomainId]);

  useEffect(() => { void refreshAll(); }, []);

  useEffect(() => {
    if (firstMailboxLoad.current) {
      firstMailboxLoad.current = false;
      return;
    }
    setLoading(true);
    void loadMailboxes(selectedDomainId, mailboxPage, mailboxPageSize)
      .catch((error) => notify(errorMessage(error, '邮箱列表加载失败'), 'error'))
      .finally(() => setLoading(false));
  }, [selectedDomainId, mailboxPage, mailboxPageSize, loadMailboxes, notify]);
  const selectedDomain = useMemo(() => domains.find((item) => item.id === selectedDomainId) || null, [domains, selectedDomainId]);
  const totals = useMemo(() => ({
    domains: domains.length,
    activeDomains: domains.filter((item) => item.status === 'active').length,
    mailboxes: domains.reduce((sum, item) => sum + item.mailboxCount, 0),
    messages: domains.reduce((sum, item) => sum + item.messageCount, 0),
  }), [domains]);

  const createDomain = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setWorking('create-domain');
    try {
      const created = await api<DomainMailDomain>('/api/domain-mail/domains', {
        method: 'POST',
        body: JSON.stringify(domainForm),
      });
      setDomainOpen(false);
      setDomainForm((current) => ({ ...current, domain: '', cloudflareToken: '' }));
      setSelectedDomainId(created.id);
      setMailboxPage(1);
      setGenerateForm((current) => ({ ...current, domainId: String(created.id) }));
      await Promise.all([loadDomains(), loadMailboxes(created.id, 1, mailboxPageSize)]);
      notify('域名已保存，可继续执行 Cloudflare 接入');
    } catch (error) {
      notify(errorMessage(error, '域名保存失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const runDomainAction = async (domain: DomainMailDomain, action: 'provision' | 'verify'): Promise<void> => {
    setWorking(`${action}-${domain.id}`);
    try {
      await api(`/api/domain-mail/domains/${domain.id}/${action}`, { method: 'POST' });
      await Promise.all([loadDomains(), loadMailboxes(selectedDomainId, mailboxPage, mailboxPageSize)]);
      notify(action === 'provision' ? 'Cloudflare 收件链路已配置' : '收件链路验证完成');
    } catch (error) {
      await loadDomains().catch(() => undefined);
      notify(errorMessage(error, action === 'provision' ? 'Cloudflare 接入失败' : '链路验证失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const removeDomain = async (domain: DomainMailDomain): Promise<void> => {
    if (!window.confirm(`删除 ${domain.domain} 及其全部随机邮箱和缓存邮件？`)) return;
    setWorking(`delete-domain-${domain.id}`);
    try {
      const result = await api<{ deleted: boolean; cleanupError?: string }>(`/api/domain-mail/domains/${domain.id}`, { method: 'DELETE' });
      if (selectedDomainId === domain.id) setSelectedDomainId(0);
      setMailboxPage(1);
      await Promise.all([loadDomains(), loadMailboxes(0, 1, mailboxPageSize)]);
      notify(result.cleanupError ? `本地已删除；Cloudflare 清理未完成：${result.cleanupError}` : '域名及其邮箱已删除', result.cleanupError ? 'error' : 'success');
    } catch (error) {
      notify(errorMessage(error, '域名删除失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const openGenerate = (domainId?: number): void => {
    const nextId = domainId || selectedDomainId || domains[0]?.id || 0;
    setGenerateForm((current) => ({ ...current, domainId: nextId ? String(nextId) : '' }));
    setGenerateOpen(true);
  };

  const generateMailboxes = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setWorking('generate');
    try {
      const payload = {
        domainId: Number(generateForm.domainId),
        count: Number(generateForm.count),
        lifecycle: generateForm.lifecycle,
        ttlMinutes: Number(generateForm.ttlMinutes),
        localLength: Number(generateForm.localLength),
        category: generateForm.category.trim() || 'safe',
      };
      const result = await api<{ items: DomainMailbox[]; total: number }>('/api/domain-mail/mailboxes/generate', { method: 'POST', body: JSON.stringify(payload) });
      setGenerateOpen(false);
      setSelectedDomainId(payload.domainId);
      setMailboxPage(1);
      await Promise.all([loadDomains(), loadMailboxes(payload.domainId, 1, mailboxPageSize)]);
      notify(`已生成 ${result.total} 个随机域名邮箱`);
    } catch (error) {
      notify(errorMessage(error, '随机邮箱生成失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const toggleMailbox = async (mailbox: DomainMailbox): Promise<void> => {
    setWorking(`toggle-${mailbox.id}`);
    try {
      await api<DomainMailbox>(`/api/domain-mail/mailboxes/${mailbox.id}`, { method: 'PATCH', body: JSON.stringify({ enabled: !mailbox.enabled }) });
      await Promise.all([loadMailboxes(selectedDomainId, mailboxPage, mailboxPageSize), loadDomains()]);
      notify(mailbox.enabled ? '邮箱已停用' : '邮箱已启用');
    } catch (error) {
      notify(errorMessage(error, '邮箱状态更新失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const removeMailbox = async (mailbox: DomainMailbox): Promise<void> => {
    if (!window.confirm(`删除邮箱 ${mailbox.email} 及其缓存邮件？`)) return;
    setWorking(`delete-mailbox-${mailbox.id}`);
    try {
      await api(`/api/domain-mail/mailboxes/${mailbox.id}`, { method: 'DELETE' });
      if (activeMailbox?.id === mailbox.id) setActiveMailbox(null);
      await Promise.all([loadMailboxes(selectedDomainId, mailboxPage, mailboxPageSize), loadDomains()]);
      notify('域名邮箱已删除');
    } catch (error) {
      notify(errorMessage(error, '邮箱删除失败'), 'error');
    } finally {
      setWorking('');
    }
  };

  const copyText = async (value: string, label: string): Promise<void> => {
    try {
      await writeClipboardText(value);
      notify(`${label}已复制`);
    } catch (error) {
      notify(`${label}复制失败：${errorMessage(error, '浏览器未允许剪贴板访问')}`, 'error');
    }
  };

  return (
    <div className="page domain-mail-page">
      <PageHeader
        eyebrow="DOMAIN INBOUND INFRASTRUCTURE"
        title="域名邮箱管理"
        description="支持根域名、二级域名、三级域名及任意层级子域名；随机生成地址，只收件、不配置 SMTP 发件。"
        actions={<><button className="button button-secondary" onClick={() => void refreshAll()} disabled={loading}><RefreshCw size={16} className={loading ? 'spin' : ''} />刷新</button><button className="button button-secondary" onClick={() => openGenerate()} disabled={!domains.length}><MailPlus size={16} />批量生成</button><button className="button button-primary" onClick={() => setDomainOpen(true)}><Globe2 size={16} />接入域名</button></>}
      />

      <section className="domain-stat-grid" aria-label="域名邮箱统计">
        <div><span>DOMAINS</span><strong>{totals.domains}</strong><small>已保存域名</small></div>
        <div><span>ROUTING</span><strong>{totals.activeDomains}</strong><small>收件链路在线</small></div>
        <div><span>ADDRESSES</span><strong>{totals.mailboxes}</strong><small>随机邮箱总数</small></div>
        <div><span>INBOUND</span><strong>{totals.messages}</strong><small>累计接收邮件</small></div>
      </section>

      <section className="domain-control-grid">
        <div className="domain-panel">
          <div className="domain-section-head"><div><span>01 / ROUTING</span><h2>多级域名接入</h2></div><small>自动发现父 Zone → 精确收件域 DNS → 共享 Worker</small></div>
          <div className="domain-list">
            {domains.map((domain) => (
              <article key={domain.id} className={`domain-card ${selectedDomainId === domain.id ? 'selected' : ''}`}>
                <button className="domain-card-main" onClick={() => { setSelectedDomainId(domain.id); setMailboxPage(1); }}>
                  <span className="domain-card-icon"><AtSign size={18} /></span>
                  <span className="domain-card-copy"><strong>{domain.domain}</strong><small>ZONE {domain.cloudflareZoneName || '待自动发现'} · {domainTypeText(domain)}</small><small>{domain.workerName || '共享 Worker 待创建'}</small></span>
                  <span className={`domain-status ${domainStatusClass(domain.status)}`}><i />{statusText[domain.status] || domain.status}</span>
                  <ChevronRight size={17} />
                </button>
                <div className="domain-routing-proof">
                  <span className={domain.emailRoutingStatus === 'active' ? 'ready' : ''}>{domain.emailRoutingStatus === 'active' ? <CheckCircle2 size={13} /> : <Clock3 size={13} />}DOMAIN MX</span>
                  <span className={domain.workerStatus === 'active' ? 'ready' : ''}>{domain.workerStatus === 'active' ? <CheckCircle2 size={13} /> : <Clock3 size={13} />}SHARED WORKER</span>
                  <span className={domain.catchAllStatus === 'active' ? 'ready' : ''}>{domain.catchAllStatus === 'active' ? <CheckCircle2 size={13} /> : <Clock3 size={13} />}ZONE CATCH-ALL</span>
                </div>
                <div className="domain-card-meta"><span>收件域 {domain.domain}</span><span>父 Zone {domain.cloudflareZoneName || '待发现'}</span><span>{domain.activeMailboxCount}/{domain.mailboxCount} 邮箱在线</span><span>{domain.messageCount} 封邮件</span><span>验证 {formatTime(domain.lastVerifiedAt)}</span></div>
                {domain.lastError ? <div className="domain-error"><XCircle size={14} />{domain.lastError}</div> : null}
                <div className="domain-card-actions">
                  <button className="button button-primary" onClick={() => void runDomainAction(domain, 'provision')} disabled={Boolean(working)}><Cloud size={15} />{working === `provision-${domain.id}` ? '配置中' : 'Cloudflare 接入'}</button>
                  <button className="button button-secondary" onClick={() => void runDomainAction(domain, 'verify')} disabled={Boolean(working) || !domain.cloudflareZoneId}><ShieldCheck size={15} />验证</button>
                  <button className="icon-button" onClick={() => void copyText(domain.webhookUrl, 'Webhook 地址')} title="复制 Webhook"><Clipboard size={16} /></button>
                  <button className="icon-button danger" onClick={() => void removeDomain(domain)} disabled={Boolean(working)} title="删除域名"><Trash2 size={16} /></button>
                </div>
              </article>
            ))}
            {!domains.length && !loading ? <div className="domain-empty"><ServerCog size={28} /><strong>还没有接入域名</strong><span>输入最终收件域名即可；smartMail 会自动向上发现 Cloudflare 父 Zone。</span><button className="button button-primary" onClick={() => setDomainOpen(true)}><Globe2 size={15} />接入第一个域名</button></div> : null}
          </div>
        </div>

        <aside className="domain-spec-panel">
          <span>RECEIVE-ONLY PIPELINE</span>
          <h2>{selectedDomain?.domain || '等待选择域名'}</h2>
          <p>根域名与任意层级子域名都可作为最终收件域；同一父 Zone 共用一个 Worker 与 catch-all，并按 message.to 精确分流。</p>
          <ol><li><b>01</b><div><strong>Domain MX</strong><span>为最终收件域配置 Email Routing DNS</span></div></li><li><b>02</b><div><strong>Shared Worker</strong><span>父 Zone 内按收件域选择 Webhook 与 HMAC 密钥</span></div></li><li><b>03</b><div><strong>smartMail Inbox</strong><span>解析 MIME，提取验证码与登录链接</span></div></li></ol>
          {selectedDomain ? <div className="domain-webhook"><span>WEBHOOK</span><code>{selectedDomain.webhookUrl}</code><button type="button" className="icon-button" onClick={() => void copyText(selectedDomain.webhookUrl, 'Webhook 地址')} aria-label="复制 Webhook 地址" title="复制 Webhook 地址"><Copy size={14} /></button></div> : null}
          <div className="receive-only-badge"><ShieldCheck size={17} /><div><strong>只收件模式</strong><span>未启用 SMTP，不提供发件能力</span></div></div>
        </aside>
      </section>

      <section className="domain-mailbox-section">
        <div className="domain-section-head mailbox-head"><div><span>02 / GENERATED ADDRESSES</span><h2>随机邮箱</h2></div><div><select value={selectedDomainId} onChange={(event) => { setSelectedDomainId(Number(event.target.value)); setMailboxPage(1); }} aria-label="按域名筛选"><option value={0}>全部域名</option>{domains.map((domain) => <option value={domain.id} key={domain.id}>{domain.domain}</option>)}</select><button className="button button-primary" onClick={() => openGenerate()} disabled={!domains.length}><MailPlus size={15} />生成邮箱</button></div></div>
        <div className="data-table-wrap domain-mailbox-table-wrap">
          <table className="data-table domain-mailbox-table">
            <thead><tr><th>邮箱地址</th><th>生命周期</th><th>有效期</th><th>收件</th><th>最近收件</th><th>状态</th><th className="action-column">操作</th></tr></thead>
            <tbody>
              {mailboxes.map((mailbox) => (
                <tr key={mailbox.id}>
                  <td><div className="mailbox-identity"><span className="mailbox-provider-icon">@</span><div><strong>{mailbox.email}</strong><small>{mailbox.category} · {mailbox.domain}</small></div></div></td>
                  <td><span className={`lifecycle-tag ${mailbox.lifecycle}`}>{mailbox.lifecycle === 'permanent' ? '永久' : '临时'}</span></td>
                  <td><div className="expiry-cell"><strong>{expiresText(mailbox)}</strong><small>{mailbox.expiresAt ? formatTime(mailbox.expiresAt) : 'NO EXPIRY'}</small></div></td>
                  <td><button className="mail-count" onClick={() => setActiveMailbox(mailbox)}><Inbox size={14} /><strong>{mailbox.messageCount}</strong></button></td>
                  <td>{formatTime(mailbox.lastReceivedAt)}</td>
                  <td><span className={`domain-status ${mailbox.enabled ? 'domain-status-active' : 'domain-status-error'}`}><i />{mailbox.enabled ? '收件中' : '已停用'}</span></td>
                  <td><div className="row-actions"><button type="button" className="icon-button" onClick={(event) => { event.stopPropagation(); void copyText(mailbox.email, '邮箱地址'); }} aria-label={`复制邮箱地址 ${mailbox.email}`} title="复制邮箱地址"><Copy size={15} /></button><button className="icon-button" onClick={() => setActiveMailbox(mailbox)} title="查看邮件"><Inbox size={15} /></button><button className="icon-button" onClick={() => void toggleMailbox(mailbox)} disabled={Boolean(working)} title={mailbox.enabled ? '停用' : '启用'}>{mailbox.enabled ? <PauseCircle size={15} /> : <PlayCircle size={15} />}</button><button className="icon-button danger" onClick={() => void removeMailbox(mailbox)} disabled={Boolean(working)} title="删除"><Trash2 size={15} /></button></div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!mailboxes.length && !loading ? <div className="table-state empty"><Inbox size={24} /><strong>暂无随机域名邮箱</strong><span>选择已接入域名后批量生成。</span></div> : null}
          {loading ? <div className="table-state"><RefreshCw className="spin" size={21} /><span>正在读取域名邮箱...</span></div> : null}
        </div>
        <div className="domain-mailbox-pagination" aria-label="域名邮箱分页">
          <span>共 {mailboxPagination.total} 个邮箱</span>
          <label>每页 <select value={mailboxPageSize} onChange={(event) => { const next = Number(event.target.value); if (!MAILBOX_PAGE_SIZE_OPTIONS.includes(next as (typeof MAILBOX_PAGE_SIZE_OPTIONS)[number])) return; setMailboxPageSize(next); setMailboxPage(1); }} aria-label="每页条数">{MAILBOX_PAGE_SIZE_OPTIONS.map((size) => <option value={size} key={size}>{size}</option>)}</select></label>
          <span>第 {mailboxPagination.page} / {mailboxPagination.totalPages} 页</span>
          <div>
            <button type="button" className="domain-mailbox-page-button" onClick={() => setMailboxPage(Math.max(1, mailboxPagination.page - 1))} disabled={loading || mailboxPagination.page <= 1}><ChevronLeft size={14} />上一页</button>
            <button type="button" className="domain-mailbox-page-button" onClick={() => setMailboxPage(Math.min(mailboxPagination.totalPages, mailboxPagination.page + 1))} disabled={loading || mailboxPagination.page >= mailboxPagination.totalPages}>下一页<ChevronRight size={14} /></button>
          </div>
        </div>
      </section>

      <Modal open={domainOpen} title="接入自有域名" onClose={() => setDomainOpen(false)} size="lg">
        <form className="modal-form" onSubmit={createDomain}>
          <label>收件域名（支持根域名、二级域名、三级域名及任意层级）<input required value={domainForm.domain} onChange={(event) => setDomainForm({ ...domainForm, domain: event.target.value })} placeholder="example.com / mail.example.com / inbox.team.example.com" autoComplete="off" /><small className="field-help">只需输入最终收件域名；smartMail 自动逐级向上发现 Cloudflare 父 Zone，不需要把子域创建为独立 Zone。</small></label>
          <label>Cloudflare API Token<input required type="password" value={domainForm.cloudflareToken} onChange={(event) => setDomainForm({ ...domainForm, cloudflareToken: event.target.value })} placeholder="仅保存在服务端加密字段中" autoComplete="new-password" /></label>
          <label>Webhook 根地址<input required value={domainForm.webhookBaseUrl} onChange={(event) => setDomainForm({ ...domainForm, webhookBaseUrl: event.target.value })} placeholder="https://mailapi.example.com" /><small className="field-help">可填写根地址或完整入站 URL；生产环境建议使用公网 HTTPS。</small></label>
          <div className="domain-token-proof"><ShieldCheck size={17} /><div><strong>Token 不回显 · 同一父 Zone 共享 Worker</strong><span>Token 写入前使用 smartMail 主密钥加密；多个收件子域不会互相覆盖 catch-all。</span></div></div>
          <div className="modal-actions"><button type="button" className="button button-secondary" onClick={() => setDomainOpen(false)}>取消</button><button className="button button-primary" disabled={working === 'create-domain'}><Globe2 size={15} />{working === 'create-domain' ? '保存中' : '保存域名'}</button></div>
        </form>
      </Modal>

      <Modal open={generateOpen} title="批量生成随机邮箱" onClose={() => setGenerateOpen(false)} size="lg">
        <form className="modal-form" onSubmit={generateMailboxes}>
          <label>域名<select required value={generateForm.domainId} onChange={(event) => setGenerateForm({ ...generateForm, domainId: event.target.value })}><option value="">请选择域名</option>{domains.map((domain) => <option value={domain.id} key={domain.id}>{domain.domain} · {statusText[domain.status] || domain.status}</option>)}</select></label>
          <div className="form-row"><label>生成数量<input required min="1" max="200" type="number" value={generateForm.count} onChange={(event) => setGenerateForm({ ...generateForm, count: event.target.value })} /></label><label>本地部分长度<input required min="6" max="32" type="number" value={generateForm.localLength} onChange={(event) => setGenerateForm({ ...generateForm, localLength: event.target.value })} /></label></div>
          <div className="form-row"><label>生命周期<select value={generateForm.lifecycle} onChange={(event) => setGenerateForm({ ...generateForm, lifecycle: event.target.value })}><option value="temporary">临时邮箱</option><option value="permanent">永久邮箱</option></select></label><label>分类<input value={generateForm.category} onChange={(event) => setGenerateForm({ ...generateForm, category: event.target.value })} placeholder="safe" /></label></div>
          {generateForm.lifecycle === 'temporary' ? <label>有效期（分钟）<input required min="5" max="43200" type="number" value={generateForm.ttlMinutes} onChange={(event) => setGenerateForm({ ...generateForm, ttlMinutes: event.target.value })} /><small className="field-help">范围 5-43200 分钟；到期后入站接口返回 410。</small></label> : null}
          <div className="generate-preview"><span>ADDRESS PREVIEW</span><code>{'x'.repeat(Math.max(6, Math.min(32, Number(generateForm.localLength) || 12)))}@{domains.find((item) => item.id === Number(generateForm.domainId))?.domain || 'example.com'}</code></div>
          <div className="modal-actions"><button type="button" className="button button-secondary" onClick={() => setGenerateOpen(false)}>取消</button><button className="button button-primary" disabled={working === 'generate' || !generateForm.domainId}><MailPlus size={15} />{working === 'generate' ? '生成中' : '生成邮箱'}</button></div>
        </form>
      </Modal>

      <DomainMessageDrawer mailbox={activeMailbox} onClose={() => setActiveMailbox(null)} notify={notify} />
    </div>
  );
}

/** 功能：读取并展示域名邮箱的本地缓存邮件，不触发 Outlook/iCloud 同步。 */
function DomainMessageDrawer({ mailbox, onClose, notify }: { mailbox: DomainMailbox | null; onClose: () => void; notify: Notify }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [active, setActive] = useState<Message | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!mailbox) return;
    setLoading(true);
    try {
      const data = await api<{ messages: Message[]; syncError: string }>(`/api/domain-mail/mailboxes/${mailbox.id}/messages?limit=100`);
      setMessages(data.messages);
      setActive((current) => data.messages.find((item) => item.id === current?.id) || data.messages[0] || null);
    } catch (error) {
      notify(errorMessage(error, '邮件读取失败'), 'error');
    } finally {
      setLoading(false);
    }
  }, [mailbox, notify]);

  useEffect(() => { setMessages([]); setActive(null); void load(); }, [load]);

  if (!mailbox) return null;
  const copy = async (value: string, label: string): Promise<void> => {
    try {
      await writeClipboardText(value);
      notify(`${label}已复制`);
    } catch (error) {
      notify(`${label}复制失败：${errorMessage(error, '浏览器未允许剪贴板访问')}`, 'error');
    }
  };

  return <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <aside className="message-drawer" aria-label={`${mailbox.email} 收件箱`}>
      <div className="drawer-head"><div><span>DOMAIN MAIL / READ ONLY</span><div className="drawer-mailbox-title"><h2>{mailbox.email}</h2><button type="button" className="icon-button" onClick={() => void copy(mailbox.email, '邮箱地址')} aria-label={`复制邮箱地址 ${mailbox.email}`} title="复制邮箱地址"><Copy size={14} /></button></div><p>{mailbox.lifecycle === 'permanent' ? '永久邮箱' : `临时邮箱 · ${expiresText(mailbox)}`} · {messages.length} 封缓存邮件</p></div><button className="icon-button" onClick={onClose} aria-label="关闭邮件抽屉"><ChevronRight size={19} /></button></div>
      <div className="drawer-toolbar"><span className="receive-only-mini"><ShieldCheck size={14} />只读收件箱</span><button className="button button-secondary" onClick={() => void load()} disabled={loading}><RefreshCw size={15} className={loading ? 'spin' : ''} />刷新</button></div>
      <div className="mail-reader">
        <div className="message-list">
          {messages.map((message) => <button key={message.id} className={active?.id === message.id ? 'active' : ''} onClick={() => setActive(message)}><span>{message.sender || '未知发件人'}</span><strong>{message.subject || '无主题'}</strong><small>{formatTime(message.receivedAt)}{message.code ? <code>{message.code}</code> : message.actionUrl ? <code>LINK</code> : null}</small></button>)}
          {!messages.length && !loading ? <div className="drawer-empty"><Inbox size={23} /><span>尚未收到邮件</span></div> : null}
        </div>
        <div className="message-content">
          {active ? <><div className="message-meta"><span>FROM</span><strong>{active.sender || '未知发件人'}</strong><time>{formatTime(active.receivedAt)}</time></div><h3>{active.subject || '无主题'}</h3>{active.code ? <div className="code-proof"><span>提取验证码</span><strong>{active.code}</strong><button type="button" className="icon-button" onClick={() => void copy(active.code, '验证码')} aria-label={`复制验证码 ${active.code}`} title="复制验证码"><Copy size={14} /></button></div> : null}{active.actionUrl ? <div className="link-proof"><div className="link-proof-head"><span>提取登录链接</span><div><a className="button button-primary" href={active.actionUrl} target="_blank" rel="noreferrer"><ExternalLink size={15} />打开</a><button type="button" className="button button-secondary" onClick={() => void copy(active.actionUrl, '登录链接')} aria-label="复制登录链接"><Clipboard size={15} />复制</button></div></div><textarea readOnly value={active.actionUrl} rows={3} onFocus={(event) => event.currentTarget.select()} /></div> : null}<pre>{active.bodyText || '该邮件没有纯文本正文。'}</pre></> : <div className="drawer-empty"><Inbox size={24} /><span>请选择一封邮件查看</span></div>}
        </div>
      </div>
    </aside>
  </div>;
}
